'use client'

import * as React from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import type { PublicUserProfile } from 'codegen/types'
import { SkeletonItem } from 'components/skeleton-item'
import { usePublicUserProfileQuery } from 'codegen/generated/user'
import { useUserCoinBalanceQuery } from 'codegen/generated/coins'
import { Link } from '@repo/web-ui/components/link'
import { Button } from '@repo/design-system-cf/components/button'
import { IconAssignmentInd, IconEdit } from '@repo/design-system-cf/components/icons'
import { useUser } from '@repo/cf-user/hooks/use-user-data'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { useCheckFollowingQuery } from '@web/gen/user'
import { VerifiedPremiumRibbon } from '@web/app/[locale]/clubhouse/[slug]/_components/verified-premium-ribbon'
import { FollowButton } from '@web/app/[locale]/clubhouse/[slug]/members/_components/follow-button'
import { decodeHtmlEntities } from 'utils/decode-html-entities'
import { formatUserName } from '../../utils/format-user-name'
import { UserNameLabel } from '../user-name-label'
import { CoinIcon } from '../icons'
import { SubUserProfileSection } from './sub-user-profile-section'
import { CopyUserIdButton } from '../copy-user-id-button'

interface Props {
  slug?: string
  isSubDetailSection?: boolean
  isShowFollowersCount?: boolean
}

// oxlint-disable-next-line complexity
export function ProfileInfoCard({ slug, isSubDetailSection }: Props) {
  const t = useTranslations('social-feed.components.feed.post')
  const user = useUser()

  // Public profile
  const {
    data: publicProfileRaw,
    isLoading: isProfileLoading,
    isFetching: isProfileFetching,
  } = usePublicUserProfileQuery(
    { slug: slug ?? user.data?.slug ?? '' },
    { select: d => d?.publicUserProfile, enabled: Boolean(slug ?? user.data?.slug) },
  )
  const publicProfile = publicProfileRaw

  const isOwn = Boolean(user.data?.id) && (!slug || slug === user.data?.slug)
  const isFollowingQueryEnabled = !isOwn && Boolean(publicProfile?.id)

  // Follow state
  const { data: following } = useCheckFollowingQuery(
    { input: { targetUserId: publicProfile?.id } },
    { enabled: isFollowingQueryEnabled },
  )

  // Name & badges
  const userName = formatUserName(
    isOwn ? user.data?.firstName : publicProfile?.firstName,
    isOwn ? user.data?.lastName : publicProfile?.lastName,
    t('user_label'),
    decodeHtmlEntities,
  )
  const superSkill = publicProfile?.superSkill ?? ''

  // Own-only bits
  const coinsQuery = useUserCoinBalanceQuery(undefined, { enabled: !!isOwn })
  const formattedBalance = React.useMemo(
    () => coinsQuery.data?.userBalance.balance.toLocaleString('en-US'),
    [coinsQuery.data?.userBalance.balance],
  )

  const avatarUrl = isOwn ? user.data?.avatarUrl : publicProfile?.avatarUrl
  const isUserLoading = isProfileLoading || isProfileFetching || !user.data

  if (isUserLoading) {
    return <SkeletonProfileHeader />
  }

  return (
    <div className="sm:pt-[98px] flex flex-col gap-4">
      {/* Header card */}
      <div className="bg-cf-white rounded-[12px] border border-[#1313151F] relative sm:pt-14 flex items-center flex-col gap-2 p-4">
        {avatarUrl ? (
          <Image
            src={avatarUrl}
            alt="Avatar"
            width={108}
            height={135}
            sizes="(min-width: 640px) 108px, 89px"
            className="absolute w-[108px] h-[135px] bg-cf-white object-cover rounded-4 -rotate-6 shadow-[0px_0.91px_2.73px_0px_#0000004D,0px_3.64px_7.27px_2.73px_#00000026] -top-[95px] translate-x-1/2 left-3.5"
          />
        ) : null}
        <div className="flex flex-row items-center justify-center text-lg font-medium">
          <UserNameLabel className="font-bold text-20 text-cf-grey-1000 leading-tight text-center">
            {isSubDetailSection ? (
              userName
            ) : (
              <Link
                href={AppRoutes.SOCIAL_FEED_CRAFTER + user.data?.slug}
                className="hover:text-cf-purple-1000"
              >
                {userName}
              </Link>
            )}
            {/* Glue: non-breaking space + icon that can’t wrap away */}
            <span className="inline-flex items-center align-baseline whitespace-nowrap">
              {'\u00A0'}
              <VerifiedPremiumRibbon
                user={
                  (isOwn
                    ? (user.data as unknown as PublicUserProfile)
                    : (publicProfile as unknown as PublicUserProfile)) as PublicUserProfile
                }
              />
            </span>
          </UserNameLabel>
        </div>
        <CopyUserIdButton userId={publicProfile?.id || ''} />
        {superSkill ? (
          <div className="inline-flex w-fit border border-[#1313151F] bg-cf-pink-100 px-2 rounded-[24px] justify-center items-center gap-0.5">
            <IconAssignmentInd className="size-3.5 text-cf-pink-800" />
            <span className="text-cf-pink-800 font-semibold text-center text-xs">{superSkill}</span>
          </div>
        ) : null}
        {isOwn && formattedBalance ? (
          <Link href="/coins/" className="flex flex-row items-center gap-0.5">
            <CoinIcon />
            <span className="ml-1 text-14 font-bold text-cf-grey-1000">{formattedBalance}</span>
          </Link>
        ) : null}
        {isOwn ? (
          <Button variant="tertiary" className="w-full mt-3">
            <Link
              href={AppRoutes.MY_ACCOUNT_PROFILE}
              className="w-full flex items-center justify-center gap-1.5"
            >
              <IconEdit className="!size-5" />
              {t('edit_profile')}
            </Link>
          </Button>
        ) : (
          <FollowButton
            following={following?.usersCheckFollowing}
            userId={publicProfile?.id as string}
            className="w-full mt-3"
            isShowUnfollowWithoutIcon
          />
        )}
      </div>

      {/* Secondary card */}
      {isSubDetailSection ? <SubUserProfileSection slug={slug} isOwn={isOwn} /> : null}
    </div>
  )
}

export function SkeletonProfileHeader() {
  return (
    <div className="sm:pt-[98px] flex flex-col gap-4">
      {/* Header card */}
      <div className="bg-cf-white rounded-[12px] border border-[#1313151F] relative sm:pt-14 flex items-center flex-col gap-2 p-4">
        {/* Avatar skeleton */}
        <SkeletonItem className="absolute w-[108px] h-[135px] rounded-4 -rotate-6 -top-[95px] translate-x-1/2 left-3.5 shadow-[0px_0.91px_2.73px_0px_#0000004D,0px_3.64px_7.27px_2.73px_#00000026]" />

        {/* Name + badge */}
        <div className="mt-3 w-full flex items-center justify-center gap-2">
          <SkeletonItem className="h-6 w-2/3 max-w-[220px] rounded" />
          <SkeletonItem className="h-5 w-16 rounded" />
        </div>

        {/* Super skill pill */}
        <div className="mt-3">
          <SkeletonItem className="h-6 w-40 rounded-[24px]" />
        </div>
        <div className="mt-3">
          <SkeletonItem className="h-6 w-28 rounded-[24px]" />
        </div>
        {/* CTA */}
        <SkeletonItem className="h-9 w-full rounded mt-2" />
      </div>
    </div>
  )
}
